<template>
  <div class="chart" ref="parentComponent">
    <div
      ref="googleChart"
      class="chart__chart"
      :class="'chart__chart--' + chartType"
      id="chart"
    ></div>
    <div
      class="chart__legend"
      ref="legend"
      id="chartData"
      v-if="chartType === 'pieChart'"
    >
      <div
        class="chart__legendItem"
        v-for="(item, i) in chartData"
        :key="item.label"
        ref="legendItems"
        v-on:mouseover="enterLegend(i)"
        v-on:mouseout="leaveLegend(i)"
        :style="{
          '--labelColor': palette[i],
          '--textColor': chartTextColors[i],
        }"
        v-html="getLegendItemLabel(item.label, item.value, state.sum)"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, onBeforeUnmount, nextTick } from "vue";

const parentComponent = ref(null);
const googleChart = ref(null);
const legend = ref(null);
const legendItems = ref([]);

const props = defineProps({
  chartData: Array,
  chartType: String,
  yAxisLabel: String,
});
const state = reactive({
  sum: 0,
});

// Chart and legend colors
const palette = [
  "#112c45",
  "#3a3c65",
  "#6e477b",
  "#a64f81",
  "#d85c78",
  "#fb7762",
  "#ff9e47",
  "#ffcd32",
];
const chartTextColors = [
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#000000",
  "#000000",
];

let chart = null;
let activeRow = null;
let dataTable = null;
let barView = null;
const chartOptions = {
  chartArea: { height: "80%", width: "80%" },
  pieSliceText: "none",
  legend: "none",
  enableInteractivity: false,
  pieSliceBorderColor: "none",
  colors: palette,
  backgroundColor: "transparent",
  tooltip: {
    trigger: "none",
  },
  annotations: {
    alwaysOutside: true,
    textStyle: {
      bold: true,
      color: "#000000",
    },
    stem: {
      color: "transparent",
    },
  },
  vAxis: {
    title: props.yAxisLabel,
    viewWindow: {
      max:
        1.3 *
        props.chartData.reduce((val, item) => {
          return val > item.value ? val : item.value;
        }, props.chartData[0].value),
    },
  },
  hAxis: {
    showTextEvery: 1,
  },
};

//Lifecycle
onMounted(async () => {
  if (props.chartType === "pieChart") {
    //register click events
    document.addEventListener("click", handleClickOutside);

    // Add values together to get the total amount of the chart
    state.sum = props.chartData.reduce((total, item) => {
      return total + item.value;
    }, 0);
  } else {
    //re-draw when screen size changes for responsiveness
    window.addEventListener(
      "resize",
      debounce(() => {
        chart.draw(barView, chartOptions);
      })
    );
  }

  // Init google chart
  google.charts.load("current", { packages: ["corechart"] });
  google.charts.setOnLoadCallback(drawChart);

  await nextTick();

  // Set max legend item width
  if (legend.value) {
    let largestLegendItem = 0;
    legendItems.value.forEach((item) => {
      if (largestLegendItem < item.offsetWidth)
        largestLegendItem = item.offsetWidth;
    });
    legend.value.style.setProperty(
      "--legendItemWidth",
      largestLegendItem + "px"
    );
  }
});
onBeforeUnmount(() => {
  if (props.chartType === "pieChart") {
    document.removeEventListener("click", handleClickOutside);
  }
});

//Methods
function drawChart() {
  dataTable = new google.visualization.DataTable();
  dataTable.addColumn("string", "label");
  dataTable.addColumn("number", "value");

  if (props.chartType === "barChart") {
    //add data
    dataTable.addColumn({ role: "style" }, "color");

    props.chartData.forEach((item, i) => {
      dataTable.addRow([item.label, item.value, palette[i]]);
    });

    barView = new google.visualization.DataView(dataTable);
    barView.setColumns([
      0,
      1,
      2,
      {
        calc: "stringify",
        sourceColumn: 1,
        type: "string",
        role: "annotation",
      },
    ]);

    // establish the bar chart
    chart = new google.visualization.ColumnChart(googleChart.value);
    // apply the bar chart to the page
    chart.draw(barView, chartOptions);
  }

  if (props.chartType === "pieChart") {
    //add data
    props.chartData.forEach((item) => {
      dataTable.addRow([item.label, item.value]);
    });

    // establish the pie chart
    chart = new google.visualization.PieChart(googleChart.value);

    // functionality for hovering over bar chart to affect the legend
    google.visualization.events.addListener(chart, "onmouseover", enterChart);
    google.visualization.events.addListener(chart, "onmouseout", leaveChart);

    // apply the pie chart to the page
    chart.draw(dataTable, chartOptions);
  }
}
function enterChart(e) {
  if (e.row !== activeRow) {
    //deactivate all legend items
    legendItems.value.forEach((i) =>
      i.classList.remove("chart__legendItem--active")
    );
    //set active row (slice)
    activeRow = e.row;
    //offset the active slice and redraw chart
    let selectedLegendItem = legendItems.value[activeRow];
    selectedLegendItem.classList.add("chart__legendItem--active");
    chartOptions.slices = { [e.row]: { offset: 0.07 } };
    chart.draw(dataTable, chartOptions);
  }
}
function leaveChart() {
  //nullify the active row, clear the legend and redraw the chart
  activeRow = null;
  legendItems.value.forEach((i) =>
    i.classList.remove("chart__legendItem--active")
  );
  chartOptions.slices = {};
  chart.draw(dataTable, chartOptions);
}
function enterLegend(index) {
  enterChart({ row: index });
}
function leaveLegend(index) {
  leaveChart({ row: index });
}
function handleClickOutside(e) {
  if (!parentComponent.value.contains(e.target)) {
    leaveChart();
  }
}
function debounce(func) {
  let timer;
  return function (event) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(func, 10, event);
  };
}
function getLegendItemLabel(label, value, sum) {
  const percent = Math.round((100 * value) / sum);
  let retval = label;
  if (percent !== value) retval += `: ${value}`;
  retval += ` (${percent}%)`;
  return retval;
}
</script>

<style lang="scss">
@use "components/loader/loader.scss";

.chartWrapper {
  padding: 1em var(--gutterX);
  border-block-start: 1px solid var(--borderColor, var(--neutral--dark));
  border-block-end: 1px solid var(--borderColor, var(--neutral--dark));
}
.chart__header {
  padding-block-end: 1em;
}
.chart__chart {
  text {
    font-size: var(--step--1);
    font-family: var(--font-family);
    font-style: normal;
  }
}
</style>

<style lang="scss" scoped>
.chart {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1em;
}
.chart__chart {
  display: flex;
  align-items: center;
}
.chart__chart--pieChart {
  width: 250px;
  height: 250px;
  flex: 0 1 250px;
}
.chart__chart--barChart {
  width: 100%;
  max-height: 300px;
  aspect-ratio: 3/2;
}
.chart__legend {
  --legendItemWidth: 232px; //initial default but gets dynamically updated on page load
  flex: 1;
  padding-block: 1em;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--legendItemWidth), 1fr));
  justify-items: start;
  gap: 0.5em 1em;
}
.chart__legendItem {
  position: relative;
  padding-inline-start: 2em;
  height: 1.5em;
  white-space: nowrap;
  display: flex;
  align-items: center;
  transition: color 0.2s ease-in;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    width: 1.5em;
    height: 1.5em;
    background-color: var(--labelColor);
    transition: width 0.2s ease-in;
    z-index: calc(var(--z) * -1);
  }

  &.chart__legendItem--active,
  &:hover {
    color: var(--textColor);
    &:before {
      width: 100%;
      right: 0;
    }
  }
}
</style>
